import React, { useState, useEffect } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { permissionActions } from '_actions';
import { getApplicantStatus } from '_utils/utils';
import { contactEmailConstants } from '_constants';
import { Button } from '_components/utils/Button/Button';
import { formatDateTime, formatDate } from '_helpers/helpers';
import ApplicantStatusCard from '_components/utils/ApplicantStatusCard/ApplicantStatusCard';
import ApplicantNotesSection from './ApplicantNotesSection';
import ApplicantRightColumnSection from './ApplicantRightColumnSection';
import ApplicantLeftColumnSection from './ApplicantLeftColumnSection';

const statusApplication = {
  NOT_SELECTED_ARCHIVE: 0,
  RECEIVED: 1,
  WAS_HIRED: 2,
  IN_REVIEW: 3,
  OFFERED_POSITION: 4,
  DO_NOT_HIRE_ARCHIVE: 10,
};

const checkNull = (value) => {
  return (
    value === null ||
    value === 'null' ||
    value === undefined ||
    value === 'undefined' ||
    value === ''
  );
};

const checkProperties = (obj) => {
  if (!obj || typeof obj !== 'object') return true;
  return Object.values(obj).every(
    (val) =>
      checkNull(val) || (typeof val === 'object' && checkProperties(val)),
  );
};

const ApplicantDetailsSection = ({ applicants }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const [reasonEditMode, setReasonEditMode] = useState(false);
  const [adminReason, setAdminReason] = useState('');
  const [interviewFormSubmissionDate, setInterviewFormSubmissionDate] =
    useState('');

  const applicantData = applicants?.data || {};
  const status = applicantData?.status;
  const loggedInUser = user?.profile?.data;
  const isSupAdmin = loggedInUser?.is_admin === 2;

  useEffect(() => {
    dispatch(permissionActions.getPermission());
  }, [dispatch]);

  useEffect(() => {
    if (applicantData?.admin_reason_for_leaving) {
      setAdminReason(applicantData.admin_reason_for_leaving);
    }

    const interviewFormDate =
      applicants?.list?.data?.interview_form_submission_date ||
      applicantData?.interview_form_submission_date;

    if (interviewFormDate && moment(interviewFormDate).isValid()) {
      setInterviewFormSubmissionDate(interviewFormDate);
    } else {
      setInterviewFormSubmissionDate('');
    }
  }, [
    applicantData?.admin_reason_for_leaving,
    applicants?.list?.data?.interview_form_submission_date,
    applicantData?.interview_form_submission_date,
    user,
  ]);

  const saveReason = () => {
    const data = new FormData();
    data.append('hash', applicantData?.hash);
    data.append('admin_reason_for_leaving', adminReason);

    dispatch(permissionActions.saveAdminReasonForLeaving(data))
      .then((res) => {
        if (res?.success) {
          toast.success('Saved reason for leaving / returning successfully.');
        } else {
          toast.error('Failed to save reason.');
        }
      })
      .catch(() => {
        toast.error('Failed to save reason.');
      });
  };

  let title = '';
  if (
    [
      statusApplication.NOT_SELECTED_ARCHIVE,
      statusApplication.DO_NOT_HIRE_ARCHIVE,
    ].includes(status)
  ) {
    title = 'Who rejected them?';
  } else if (status === statusApplication.WAS_HIRED) {
    title = 'Who selected them?';
  }

  return (
    <div className='applicant-view-container'>
      <h3 className='left'>
        Full Application:{' '}
        {applicantData
          ? `${applicantData.first_name || ''} ${applicantData.middle_initial || ''} ${applicantData.last_name || ''}`
          : ''}
      </h3>
      <h3 className='right'>Date Applied: {applicantData?.date || ''}</h3>

      {loggedInUser &&
        applicantData?.former_employee_approval !== 0 &&
        [
          contactEmailConstants.HR,
          contactEmailConstants.JIMMY_DO,
          contactEmailConstants.TEST_ADMIN,
        ].includes(loggedInUser?.email) && (
          <>
            <h4>REASON FOR LEAVING/RETURNING</h4>
            <textarea
              onClick={() => {
                setReasonEditMode(
                  [
                    contactEmailConstants.HR,
                    contactEmailConstants.TEST_ADMIN,
                  ].includes(loggedInUser?.email),
                );
              }}
              className='add-edit-note'
              rows='4'
              onChange={(e) => setAdminReason(e.target.value)}
              value={adminReason}
              placeholder='Reason for leaving / returning'
              autoComplete='off'
              maxLength={1200}
              disabled={[contactEmailConstants.JIMMY_DO].includes(
                loggedInUser?.email,
              )}
            />
            {reasonEditMode && (
              <div className='buttons'>
                <Button
                  name='Save Reason'
                  fontColor='#fff'
                  backgroundColor='#297a18'
                  loading={false}
                  onClick={saveReason}
                  disabled={!adminReason}
                />
                &nbsp;
                <Button
                  name='Cancel'
                  fontColor='#fff'
                  backgroundColor='#747474'
                  onClick={() => setReasonEditMode(false)}
                />
              </div>
            )}
          </>
        )}

      <div className='applicant-status-details'>
        <ApplicantStatusCard
          title='Hiring Status'
          content={getApplicantStatus?.[status]}
        />

        {status === statusApplication.WAS_HIRED && (
          <ApplicantStatusCard
            title='Crew'
            content={applicantData?.crew?.resource_name || ''}
          />
        )}

        {[
          statusApplication.WAS_HIRED,
          statusApplication.NOT_SELECTED_ARCHIVE,
          statusApplication.DO_NOT_HIRE_ARCHIVE,
        ].includes(status) && (
          <ApplicantStatusCard
            title={title}
            content={applicantData?.performed_user?.full_name_last_first || ''}
          />
        )}

        {[
          statusApplication.NOT_SELECTED_ARCHIVE,
          statusApplication.DO_NOT_HIRE_ARCHIVE,
        ].includes(status) && (
          <ApplicantStatusCard
            title='Notes on why the application was rejected'
            content={applicantData?.rejected_reason || ''}
          />
        )}

        {[
          statusApplication.WAS_HIRED,
          statusApplication.NOT_SELECTED_ARCHIVE,
          statusApplication.DO_NOT_HIRE_ARCHIVE,
        ].includes(status) && (
          <ApplicantStatusCard
            title='Date and time'
            content={formatDateTime(applicantData?.status_change_date)}
          />
        )}
      </div>

      {applicantData?.interview_form_count > 0 && (
        <div>
          <h4>
            <b>
              Interview Form Submitted Date:{' '}
              {interviewFormSubmissionDate
                ? formatDate(interviewFormSubmissionDate)
                : '-'}
            </b>
          </h4>
        </div>
      )}

      <ApplicantNotesSection
        applicants={applicants}
        isSupAdmin={isSupAdmin}
        loggedInUser={loggedInUser}
      />

      <ApplicantLeftColumnSection
        applicants={applicants}
        isSupAdmin={isSupAdmin}
      />

      <ApplicantRightColumnSection
        applicants={applicants}
      />
    </div>
  );
};

ApplicantDetailsSection.propTypes = {
  applicants: PropTypes.object.isRequired,
};


export default ApplicantDetailsSection;
